import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import edit from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import SitesFilterService from 'src/services/sites/sitesFilterService';
import {
  setCurrentPage,
  setIsCreateModalOpen,
  setSelectedSite,
  setTotalPages,
} from 'src/store/adminConfigPageSlice/siteConfigPageSlice/siteConfigPageSlice';
import { type TableDataRow } from 'src/store/adminConfigPageSlice/siteConfigPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

import { COLUMNS } from './constants';

const SiteTable = () => {
  const dispatch = useDispatch();
  const [sortColumn, setSortColumn] = useState<keyof TableDataRow>('site');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const onSort = useCallback(
    ({ sortColumn, sortDirection }: { sortColumn: keyof TableDataRow; sortDirection: TableSortDirection }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    [],
  );
  const { tableData, appliedFilterOptions, searchField, currentPage, perPage } = useAppSelector(
    (state) => state.siteConfigPage,
  );
  const [filteredAndSortedTableData, setFilteredAndSortedTableData] = useState(tableData);

  useEffect(() => {
    // filter, sorting, and pagination
    const filteredData = SitesFilterService.filterSites(tableData, appliedFilterOptions);
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      filteredData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setFilteredAndSortedTableData(paginatedData);
    dispatch(setTotalPages(totalPages));
  }, [tableData, appliedFilterOptions, searchField, sortColumn, sortDirection, currentPage, perPage, dispatch]);

  useEffect(() => {
    // go back to first page when filter/sort
    dispatch(setCurrentPage(1));
  }, [dispatch, searchField, sortColumn, sortDirection]);

  return (
    <Box
      overflowY="scroll"
      overflowX="scroll"
      type="outline"
      id="site-config-data-table"
      className="view-and-edit-tasks__table"
    >
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        {/* Header */}
        <TableRow>
          {COLUMNS.map(({ sortKey, name, width = '' }, idx) => (
            <TableCell width={width} key={`${idx}-${sortKey}`} sortColumn={sortKey && sortKey}>
              {name}
            </TableCell>
          ))}
        </TableRow>

        {/* Body */}
        {filteredAndSortedTableData.map((row) => {
          const tldHeadcount: number = row.tldHeadcount ? row.tldHeadcount : Math.ceil(row.headcount * 0.05);
          return (
            <UncontrolledExpandableRow
              key={row.id}
              dataCy={`${row.site}-site-row`}
              data-testid="site-config-table__row"
            >
              <TableCell data-cy={`${row.site}-site-name`}>{row.site}</TableCell>
              <TableCell>{row.country}</TableCell>
              <TableCell>{row.org}</TableCell>
              <TableCell>{row.subOrg}</TableCell>
              <TableCell>{row.buildingType}</TableCell>
              <TableCell>{row.region}</TableCell>
              <TableCell>{row.isActive ? 'Yes' : 'No'}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>{row.headcount}</TableCell>
              <TableCell>{tldHeadcount}</TableCell>
              <TableCell>{row.siteCoLocations}</TableCell>
              <TableCell data-cy={`${row.site}-decision-hub-participating-in-pilot`}>
                {row.participatingInDecisionHubPilot ? 'Yes' : 'No'}
              </TableCell>
              <TableCell data-cy={`${row.site}-atoz-participating-in-pilot`}>
                {row.atozNotificationsEnabled ? 'Yes' : 'No'}
              </TableCell>
              <TableCell data-cy={`${row.site}-job-mod-participating-in-pilot`}>
                {row.jobModificationsEnabled ? 'Yes' : 'No'}
              </TableCell>
              <TableCell>
                <Button
                  type="icon"
                  onClick={() => {
                    dispatch(setIsCreateModalOpen(true));
                    dispatch(setSelectedSite(row));
                  }}
                  data-cy={`${row.site}-site-edit-button`}
                >
                  <Icon tokens={edit} />
                </Button>
              </TableCell>
            </UncontrolledExpandableRow>
          );
        })}
      </Table>
    </Box>
  );
};

export default SiteTable;
